import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClipboardDocumentListIcon,
  TruckIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import {
  formatNumber,
  formatDistanceToNow,
  getPriorityColor,
  getStatusColor,
} from "../utils";

const AdminDashboard = ({ data }) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-dark-300">{t("dashboard.noData")}</p>
      </div>
    );
  }

  const { overview, charts, performance, recent } = data;

  const stats = [
    {
      name: t("dashboard.stats.totalTasks"),
      value: formatNumber(overview.totalTasks),
      icon: ClipboardDocumentListIcon,
      color: "bg-blue-600",
      href: "/tasks",
    },
    {
      name: t("dashboard.stats.activeTasks"),
      value: formatNumber(overview.activeTasks),
      icon: ClockIcon,
      color: "bg-yellow-600",
      href: "/tasks?status=in-progress",
    },
    {
      name: t("dashboard.stats.totalCars"),
      value: formatNumber(overview.totalCars),
      icon: TruckIcon,
      color: "bg-green-600",
      href: "/cars",
    },
    {
      name: t("dashboard.stats.mechanics"),
      value: formatNumber(overview.totalMechanics),
      icon: UsersIcon,
      color: "bg-purple-600",
      href: "/users?role=mechanic",
    },
    {
      name: t("dashboard.stats.overdueTasks"),
      value: formatNumber(overview.overdueTasks),
      icon: ExclamationTriangleIcon,
      color: "bg-red-600",
      href: "/tasks?status=overdue",
    },
    {
      name: t("dashboard.stats.highPriority"),
      value: formatNumber(overview.highPriorityTasks),
      icon: ExclamationTriangleIcon,
      color: "bg-orange-600",
      href: "/tasks?priority=high",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="relative bg-dark-800 border border-dark-700 rounded-lg p-6 hover:bg-dark-700 transition-colors"
          >
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-dark-300">{stat.name}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Status Chart */}
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4">
            {t("dashboard.charts.tasksByStatus")}
          </h3>
          <div className="space-y-3">
            {charts.tasksByStatus.map((item) => (
              <div key={item._id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      getStatusColor(item._id).split(" ")[1]
                    } mr-3`}
                  ></div>
                  <span className="text-sm text-dark-300 capitalize">
                    {item._id}
                  </span>
                </div>
                <span className="text-sm font-medium text-white">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Task Priority Chart */}
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4">
            {t("dashboard.charts.tasksByPriority")}
          </h3>
          <div className="space-y-3">
            {charts.tasksByPriority.map((item) => (
              <div key={item._id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      getPriorityColor(item._id).split(" ")[1]
                    } mr-3`}
                  ></div>
                  <span className="text-sm text-dark-300 capitalize">
                    {item._id}
                  </span>
                </div>
                <span className="text-sm font-medium text-white">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance and Recent Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Mechanics */}
        {performance?.mechanicsPerformance && (
          <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">
              {t("dashboard.performance.topMechanics")}
            </h3>
            <div className="space-y-4">
              {performance.mechanicsPerformance.slice(0, 5).map((mechanic) => (
                <div
                  key={mechanic._id}
                  className="flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-white">
                      {mechanic.name}
                    </p>
                    <p className="text-xs text-dark-400">{mechanic.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">
                      {mechanic.completedTasks}{" "}
                      {t("dashboard.performance.tasks")}
                    </p>
                    <p className="text-xs text-dark-400">
                      {Math.round(mechanic.averageTime / 60)}m{" "}
                      {t("dashboard.performance.avgTime")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Tasks */}
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">
              {t("dashboard.recent.recentTasks")}
            </h3>
            <Link
              to="/tasks"
              className="text-sm text-primary-500 hover:text-primary-400"
            >
              {t("dashboard.recent.viewAll")}
            </Link>
          </div>
          <div className="space-y-4">
            {recent.recentTasks.slice(0, 5).map((task) => (
              <div key={task._id} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-white truncate">
                    {task.title}
                  </p>
                  <p className="text-xs text-dark-400">
                    {t("dashboard.recent.assignedTo")} {task.assignedTo.name} •{" "}
                    {formatDistanceToNow(task.createdAt)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                      task.priority
                    )}`}
                  >
                    {task.priority}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      task.status
                    )}`}
                  >
                    {task.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Maintenance Alerts */}
      {(overview.maintenanceDue > 0 || overview.upcomingMaintenance > 0) && (
        <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-yellow-200">
                {t("dashboard.maintenance.alerts")}
              </h3>
              <p className="text-sm text-yellow-300 mt-1">
                {overview.maintenanceDue > 0 &&
                  `${overview.maintenanceDue} ${t(
                    "dashboard.maintenance.needImmediate"
                  )}`}
                {overview.maintenanceDue > 0 &&
                  overview.upcomingMaintenance > 0 &&
                  " • "}
                {overview.upcomingMaintenance > 0 &&
                  `${overview.upcomingMaintenance} ${t(
                    "dashboard.maintenance.upcoming"
                  )}`}
              </p>
            </div>
            <Link
              to="/cars?filter=maintenance"
              className="ml-auto btn btn-warning"
            >
              {t("dashboard.maintenance.viewCars")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
